'use client'

import { ReactElement, useEffect } from 'react'
import { formatAmountForDisplay, getDiscount } from '@/utils/stripe-helpers'
import { CURRENCY } from '@/config'
import { ProductDetail, useProductStore } from '@/hooks/use-cart-product/store'
import {
  customerDiscountSelector,
  productFieldsSelector,
  productsSelector,
  promotionCodeSelector,
  setCustomerDiscountsSelector,
  setPromotionSelector,
} from '@/hooks/use-cart-product/selectors'
import { Discount } from '@/components/icons/discount-icon'
import { CloseCross, Info, Placeholder } from '@repo/ui'
import { useTrackingStore } from '@/hooks/use-tracking/store'
import { qParamsSelector } from '@/hooks/use-tracking/selectors'
import { twMerge } from 'tailwind-merge'
import { Tooltip } from '@repo/ui/components/tooltip'
import PopoverTooltip from '@repo/ui/components/popover/popover-tooltip'
import { getCustomerDiscounts } from '@/actions/stripe'
import { useCustomerStore } from '@/hooks/use-customer/store'
import { customerSelector } from '@/hooks/use-customer/selectors'
import { useTaxRegion } from '@/hooks/use-tax-region'
import {
  TAX_LINE_GENERIC_LABEL,
} from '@/config/textos-fiscales'

export type PaymentDetailProps = {
  prods: ProductDetail
  amount: number
  subscription: number
  period: string
}

export function PaymentDetail({
  prods,
  amount,
  subscription,
  period,
}: PaymentDetailProps): ReactElement {
  const promotion = useProductStore(promotionCodeSelector)
  const customerDiscountss = useProductStore(customerDiscountSelector)
  const setPromotion = useProductStore(setPromotionSelector)
  const { deleteKeyFromParam } = useTrackingStore(qParamsSelector)
  const customerStore = useCustomerStore(customerSelector)
  const productStore = useProductStore(productFieldsSelector)
  const prodSelected = productStore.currentProducts
  const setCustomerDiscouts = useProductStore(setCustomerDiscountsSelector)
  // Manda la provincia del paso 2 (decisión D3). Mientras no la haya, el
  // desglose se calcula al 21% pero la línea no afirma qué impuesto es.
  const { taxRegion } = useTaxRegion()

  const {
    promoDiscount: discount,
    tax,
    subTotal,
    total,
    customerSubDiscount,
    customerBalance,
  } = getDiscount(prods, promotion, customerDiscountss, taxRegion)

  const deleteCode = () => {
    deleteKeyFromParam('promo')
    setPromotion(null)
  }

  if (discount === 0 && promotion?.code) {
    deleteCode()
  }

  useEffect(() => {
    const callApi = async () => {
      return await getCustomerDiscounts({
        email: customerStore.email,
        dniNie: customerStore.dninie || undefined,
        academy: prodSelected.productsList[0].academy,
        elite: true,
      })
    }

    if (customerStore?.email && prodSelected?.productsList?.length > 0) {
      callApi().then((res) => {
        if (res) {
          setCustomerDiscouts({
            customerBalance: res.balance,
            customerSubDiscount: res.currentSubAmount,
          })
        }
      })
    } else {
      setCustomerDiscouts({
        customerBalance: 0,
        customerSubDiscount: 0,
      })
    }
  }, [customerStore?.email, customerStore?.dninie, prodSelected?.productsList])

  return (
    <div className="flex flex-col space-y-2">
      {discount && (
        <div className="flex items-center md:px-2">
          <div className="w-full">
            <div className="bg-checkout-green-light flex max-w-[260px] flex-row items-center gap-2 p-2">
              <Discount
                color={'green'}
                css={{
                  width: 24,
                  height: 16,
                }}
              />
              <p className="checkout-s text-checkout-green-dark w-full font-medium">
                {`${promotion.code} `}aplicado
              </p>
              <div onClick={deleteCode}>
                <CloseCross className="cursor-pointer fill-[#238037] stroke-[#238037]" />
              </div>
            </div>
          </div>
          <div className="flex w-1/3 flex-col items-end">
            <p className="checkout-s md:text-checkout-mxl font-medium text-[#238037]">
              -{formatAmountForDisplay(discount, CURRENCY)}
            </p>
          </div>
        </div>
      )}
      <div className="flex flex-col space-y-3">
        {customerBalance !== 0 && (
          <PaymentLine
            description={'Saldo Ninja (Referidos u otros)'}
            infoDescription={
              'Saldo que puedes haber acumulado por otras promociones'
            }
            amount={customerBalance}
          />
        )}
        {customerSubDiscount !== 0 && (
          <PaymentLine
            description={'Tiempo no utilizado de suscripción actual'}
            infoDescription={
              'Tiempo restante de tu suscripción actual que no has utilizado aún. Puedes aplicarlo como crédito a tu nueva suscripción.'
            }
            amount={customerSubDiscount}
          />
        )}
        <PaymentLine description={'Subtotal'} amount={subTotal} />
        <PaymentLine description={TAX_LINE_GENERIC_LABEL} amount={tax} />
        <div className="flex flex-col rounded-md border bg-[#D9D9D9] bg-opacity-15 px-2 py-4 md:hidden">
          <div className="flex flex-row">
            <div className="w-full">
              <p className="text-lg font-semibold">Total a pagar hoy</p>
            </div>
            <div className="flex w-1/3 flex-col items-end">
              {total === null ? (
                <Placeholder
                  className="w-full rounded"
                  style={{ height: 30, width: 100 }}
                />
              ) : (
                <p className="text-checkout-xxl font-semibold">
                  {formatAmountForDisplay(total, CURRENCY)}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-row">
            <p className="text-checkout-m text-checkout-bk-op40 font-normal">
              Luego{' '}
              <span>{formatAmountForDisplay(subscription, CURRENCY)}</span>{' '}
              {period}
            </p>
          </div>
        </div>
        <div className="border-checkout-bk-op15 hidden flex-row rounded-md border bg-[#D9D9D9] bg-opacity-15 px-4 py-5 text-xl font-bold sm:flex">
          <div className="w-full">
            <p className="text-checkout-xl font-medium">Total</p>
          </div>
          <div className="flex w-1/3 flex-col items-end">
            {total === null ? (
              <Placeholder
                className="w-full rounded"
                style={{ height: 30, width: 100 }}
              />
            ) : (
              <p className="text-checkout-xxxl font-semibold">
                {formatAmountForDisplay(total, CURRENCY)}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function PaymentLine({
  description,
  amount,
  infoDescription,
}: {
  description: string
  amount: number
  infoDescription?: string
}): ReactElement {
  const negativeAmount = amount < 0

  return (
    <div
      className={twMerge(
        'flex flex-row items-center md:px-2',
        negativeAmount && 'text-[#238037]'
      )}>
      <div className="flex w-full flex-row gap-2">
        <p className="text-checkout-m text-pretty font-normal">{description}</p>
        {infoDescription && (
          <PopoverTooltip
            showArrow={true}
            showCross={true}
            content={
              <Info
                height={16}
                width={16}
                min-width={16}
                min-height={16}
                color={negativeAmount ? '#238037' : undefined}
              />
            }
            hiddenContent={<div>{infoDescription}</div>}
          />
        )}
      </div>
      <div className="flex w-1/3 flex-col items-end">
        {amount === null ? (
          <Placeholder
            className="w-full rounded"
            style={{ height: 30, width: 100 }}
          />
        ) : (
          <p className="md:text-checkout-mxl text-base font-medium">
            {formatAmountForDisplay(amount, CURRENCY)}
          </p>
        )}
      </div>
    </div>
  )
}
